import React, { useEffect, useMemo, useRef, useState } from 'react';
import JoditEditor from 'jodit-react';

const EditorJ = ({
    placeholder,
    onChange,
    onSave,
    onPrint,
    onPreview,      // viene utilizzato per i referti, dove ti fa vedere sia l'intestazione che la firma.
    onFse,
    content,         // contenuto iniziale che arriva dal padre
    adattaStyle = true,
    nascondiSalva = false,
    abilitaFse = false, // abilita il bottone FSE
    interlinea = '1.2'
}) => {
    const editor = useRef(null);
    const [isProcessing, setIsProcessing] = useState(false);

    // Usiamo *un solo* state che rappresenta il contenuto dell'editor
    const [value, setValue] = useState(content || "");

    // Se vogliamo aggiornare l'editor *solo* quando "content" cambia dall'esterno:
    const isInternalChange = useRef(false);

    useEffect(() => {
        if (!isInternalChange.current) {
            setValue(content || "");
        }
        isInternalChange.current = false;
    }, [content]);

    const handleEditorChange = (newContent) => {
        isInternalChange.current = true;
        onChange && onChange(newContent);
    };

    const [zoomLevel, setZoomLevel] = useState(1);

    // I bottoni di default
    const [bottoniDefault, setBottoniDefault] = useState([
        'save', 'print', 'preview', 'undo', 'redo', '|', 'headerDropdown',
        'font', 'fontsize',
        'bold', 'underline', 'italic', 'align', 'ul', 'ol', 'lineHeight',
        'indent', 'outdent', 'link',
        'table', 'brushCell', 'pageBreak',
        'source',
        'zoomIn', 'zoomOut',
    ]);

    // abilita fse 
    useEffect(() => {
        if (abilitaFse) {
            // se abilita fse è true, evito di fare lo
            // stampa referto cliccando su salva o fse, li setto 
            // anche come stampati
            setBottoniDefault([
                'save', 'FSE', 'print', 'anteprima', 'undo', 'redo', '|', 'headerDropdown',
                'font', 'fontsize',
                'bold', 'underline', 'italic', 'align', 'ul', 'ol', 'lineHeight',
                'indent', 'outdent', 'link',
                'table', 'brushCell', 'pageBreak',
                'source',
                'zoomIn', 'zoomOut',
            ])
        };

    }, []);

    // Se nascondo il bottone "Salva"
    useEffect(() => {
        if (nascondiSalva) {
            setBottoniDefault([
                'preview', 'undo', 'redo', '|', 'headerDropdown',
                'font', 'fontsize',
                'bold', 'underline', 'italic', 'align', 'ul', 'ol', 'lineHeight',
                'indent', 'outdent', 'link',
                'table', 'brushCell', 'pageBreak',
                'source',
                'zoomIn', 'zoomOut',
            ]);
        }
    }, [nascondiSalva]);

    const removeTrailingBr = (block) => {
        // rimuove <br> finali (anche multipli) e &nbsp; “di coda”
        while (block.lastChild) {
            const n = block.lastChild;

            // BR finale
            if (n.nodeType === 1 && n.tagName === 'BR') {
                block.removeChild(n);
                continue;
            }

            // testo finale vuoto / nbsp
            if (n.nodeType === 3) {
                const cleaned = n.nodeValue.replace(/\u00a0/g, ' ').trim();
                if (cleaned === '') {
                    block.removeChild(n);
                    continue;
                }
            }

            // se è un elemento che contiene solo <br> (raro, ma succede con formattazioni)
            if (n.nodeType === 1) {
                const html = (n.innerHTML || '').replace(/&nbsp;/g, ' ').trim().toLowerCase();
                if (html === '<br>' || html === '<br/>' || html === '<br />') {
                    block.removeChild(n);
                    continue;
                }
            }

            break;
        }
    };

    const interlineaFinale = useMemo(() => {
        const v = interlinea ?? '1.2';
        return String(v).trim() || '1.2';
    }, [interlinea]);

    const config = useMemo(() => ({
        language: 'it',
        readonly: false,
        placeholder: placeholder || "",
        askBeforePasteHTML: false,
        enter: 'br',            // Enter inserisce <br> invece di creare un nuovo blocco
        enterBlock: 'p',       // eventuali wrap di testo sciolto in <p> (fallback)
        toolbar: true,
        toolbarSticky: true,
        toolbarStickyOffset: 80,
        toolbarButtonSize: 'large',
        defaultLineHeight: interlineaFinale,
        buttons: bottoniDefault,
        events: {
            afterInit: (ed) => {
                if (!ed || !ed.editorDocument) return;

                // =========================
                // 1) CSS nel documento iframe
                // =========================
                const styleTag = ed.editorDocument.createElement("style");
                styleTag.innerHTML = `
    .jodit-wysiwyg { line-height: ${interlineaFinale}; }

    .page-break{
      border-top: 2px dashed #999;
      margin: 14px 0;
      height: 0;
    }

    @media print{
      .page-break{
        break-after: page;
        page-break-after: always;
        border: 0;
        margin: 0;
      }
      .jodit-wysiwyg{
        line-height: ${interlineaFinale};
      }
    }
  `;
                ed.editorDocument.head.appendChild(styleTag);

                // =========================
                // 2) Helper: selection SAFE (NO ed.editorWindow)
                // =========================
                const getSel = () => {
                    const win = ed.editorDocument?.defaultView;
                    return win?.getSelection ? win.getSelection() : null;
                };

                const isIn = (node, selector) => node && node.closest && node.closest(selector);

                const removeTrailingBrLocal = (block) => {
                    while (block.lastChild) {
                        const n = block.lastChild;

                        if (n.nodeType === 1 && n.tagName === "BR") {
                            block.removeChild(n);
                            continue;
                        }

                        if (n.nodeType === 3) {
                            const cleaned = n.nodeValue.replace(/\u00a0/g, " ").trim();
                            if (cleaned === "") {
                                block.removeChild(n);
                                continue;
                            }
                        }

                        if (n.nodeType === 1) {
                            const html = (n.innerHTML || "").replace(/&nbsp;/g, " ").trim().toLowerCase();
                            if (html === "<br>" || html === "<br/>" || html === "<br />") {
                                block.removeChild(n);
                                continue;
                            }
                        }

                        break;
                    }
                };

                const blockEndsWithBr = (block) => {
                    const html = (block.innerHTML || "")
                        .replace(/&nbsp;/g, " ")
                        .replace(/\uFEFF/g, "")  // ← strip del carattere invisibile inserito da Jodit
                        .trim();
                    return /<br\s*\/?>\s*$/i.test(html) || /^<br\s*\/?>$/i.test(html);
                };

                // =========================
                // 3) LA TUA LOGICA ENTER
                //    - 1° Enter -> <br>
                //    - Enter successivo se c'è già <br> finale -> crea <p>
                //    (senza vincolo isCaretAtEndOf)
                // =========================
                ed.events.on("keydown", (e) => {
                    if (e.key !== "Enter") return;
                    if (e.shiftKey) return;
                    
                    const cur = ed.selection?.current?.();
                    if (!cur) return;

                    if (isIn(cur, "li") || isIn(cur, "td,th")) return;

                    const root = ed.editor; // 👈 IMPORTANTISSIMO

                    let block = cur;

                    while (block && block !== root) {
                        if (block.tagName === "P" || block.tagName === "DIV") break;
                        block = block.parentNode;
                    }
                    // sicurezza
                    if (!block || block === root) return;
                    if (!block) return;
                    
                    // ✅ Enter successivo: se il blocco termina già con <br> -> crea nuovo <p>
                    if (blockEndsWithBr(block)) {
                        console.log("🔴 SECONDO ENTER - blockEndsWithBr è TRUE");
                        e.preventDefault();
                        console.log("   ✅ Step 1: preventDefault() fatto");

                        // togli i <br> finali (quelli del primo Enter)
                        removeTrailingBrLocal(block);
                        console.log("   ✅ Step 2: removeTrailingBrLocal() fatto");
                        console.log("      block.outerHTML adesso:", block.outerHTML.substring(0, 100));

                        // ✅ DEBUG: controlla block.parentNode
                        console.log("   🔍 Step 3: Controllo block.parentNode");
                        console.log("      block.parentNode:", block.parentNode);
                        console.log("      block.parentNode?.tagName:", block.parentNode?.tagName);

                        if (!block.parentNode) {
                            console.error("   ❌ ERRORE: block.parentNode è NULL - RETURN");
                            return false;
                        }
                        console.log("   ✅ block.parentNode esiste");

                        const newP = ed.editorDocument.createElement("p");
                        console.log("   ✅ Step 4: newP creato");

                        newP.innerHTML = "";
                        console.log("   ✅ Step 5: newP.innerHTML = '' impostato");

                        console.log("   🔍 Step 6: Prima di insertBefore");
                        console.log("      block:", block.tagName);
                        console.log("      block.nextSibling:", block.nextSibling);

                        try {
                            block.parentNode.insertBefore(newP, block.nextSibling);
                            console.log("   ✅ Step 7: insertBefore riuscito - newP inserito nel DOM");
                        } catch (err) {
                            console.error("   ❌ ERRORE in insertBefore:", err.message);
                            console.error("      Stack:", err.stack);
                            return false;
                        }

                        const range = ed.editorDocument.createRange();
                        range.setStart(newP, 0);
                        range.collapse(true);
                        console.log("   ✅ Step 8: Range creato e collassato");

                        const sel = getSel();
                        console.log("   🔍 Step 9: getSel():", sel ? "✅ EXISTS" : "❌ NULL");

                        if (sel) {
                            sel.removeAllRanges();
                            sel.addRange(range);
                            console.log("   ✅ Step 10: Cursore spostato nel nuovo <p>");
                        } else {
                            console.log("   ❌ getSel() è NULL - Cursore NON spostato");
                        }

                        console.log("🔴 SECONDO ENTER - TERMINATO\n");
                        return true;
                    }

                    // ✅ 1° Enter normale -> <br>
                    e.preventDefault();
                    //ed.s.insertHTML("<br>");
                    return true;
                });

                // =========================
                // 4) ANTI-JUMP PATCH (robusto) - NON tocca la tua logica
                // =========================
                if (!ed.__antiJumpInstalled) {
                    ed.__antiJumpInstalled = true;

                    const doc = ed.editorDocument;

                    // patch scrollIntoView 1 sola volta
                    if (!window.__pocr_origScrollIntoView) {
                        window.__pocr_origScrollIntoView = Element.prototype.scrollIntoView;
                        Element.prototype.scrollIntoView = function (...args) {
                            if (window.__pocr_blockScrollIntoView) return;
                            return window.__pocr_origScrollIntoView.apply(this, args);
                        };
                    }
                }
            }
        },
        controls: {
            headerDropdown: {
                name: 'Titolo',
                tooltip: 'Seleziona Intestazione',
                list: {
                    p: 'Paragrafo',
                    h1: 'Titolo H1',
                    h2: 'Titolo H2',
                    h3: 'Titolo H3',
                    h4: 'Titolo H4',
                    h5: 'Titolo H5',
                    h6: 'Titolo H6'
                },
                exec: (editor, current, control) => {
                    const headerTag = control.control.args && control.control.args[0];
                    if (headerTag) {
                        editor.selection.commitStyle({ element: headerTag });
                    }
                }
            },
            save: {
                name: 'Salva',
                icon: 'save',
                tooltip: 'Salva contenuto lasciandolo nelle bozze',
                exec: (editor) => {
                    setIsProcessing(true);
                    const editorContent = editor.value;
                    onSave && onSave(editorContent, 1);
                    Promise.resolve(onSave && onSave(editorContent, 1))
                        .finally(() => setIsProcessing(false));
                }
            },
            FSE: {
                name: 'FSE',
                icon: '',
                tooltip: 'Prepara il referto per l\'invio al FSE, dopo il salvataggio vai nella sezione FSE',
                exec: (editor) => {
                    setIsProcessing(true);
                    const editorContent = normalizeTableBorders(editor.value);
                    onFse && onFse(editorContent, 1);
                    Promise.resolve(onFse && onFse(editorContent, 1))
                        .finally(() => setIsProcessing(false));
                }
            },
            anteprima: {
                name: 'Anteprima',
                icon: 'eye',
                tooltip: 'Anteprima di stampa',
                exec: (editor) => {
                    const editorContent = normalizeTableBorders(editor.value);
                    onPreview && onPreview(editorContent, 2);
                }
            },
            print: {
                name: 'Stampa',
                icon: 'print',
                tooltip: 'Stampa',
                exec: (editor) => {
                    setIsProcessing(true);
                    const editorContent = normalizeTableBorders(editor.value);
                    onPrint && onPrint(editorContent, 1);
                    Promise.resolve(onPrint && onPrint(editorContent, 1))
                        .finally(() => setIsProcessing(false));
                }
            },
            pageBreak: {
                name: 'Interruzione',
                tooltip: 'Inserisce un’interruzione pagina',
                exec: (editor) => {
                    // un blocco "segnaposto" + un paragrafo vuoto dopo
                    const html = `<div class="page-break" data-page-break="1">
                    </div><p><br></p>`;

                    if (editor?.s?.insertHTML) editor.s.insertHTML(html);
                    else if (editor?.selection?.insertHTML) editor.selection.insertHTML(html);
                    else editor.value = (editor.value || '') + html;
                }
            },
            lineHeight: {
                list: [1, 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.8, 2, 2.5, 3],
                tooltip: 'Seleziona interlinea',
            },
            zoomIn: {
                name: 'Zoom +',
                icon: 'plus',
                tooltip: 'Ingrandisci contenuto',
                exec: () => {
                    setZoomLevel((prevZoom) => {
                        const newZoom = Math.min(prevZoom + 0.1, 2);
                        document.querySelector('.jodit-workplace').style.transform = `scale(${newZoom})`;
                        document.querySelector('.jodit-workplace').style.transformOrigin = 'top left';
                        return newZoom;
                    });
                }
            },
            zoomOut: {
                name: 'Zoom -',
                icon: '<svg width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M5 11h14v2H5z"/></svg>',
                tooltip: 'Riduci contenuto',
                exec: () => {
                    setZoomLevel((prevZoom) => {
                        const newZoom = Math.max(prevZoom - 0.1, 0.5);
                        document.querySelector('.jodit-workplace').style.transform = `scale(${newZoom})`;
                        document.querySelector('.jodit-workplace').style.transformOrigin = 'top left';
                        return newZoom;
                    });
                }
            }
        },
        height: "100%",
        minHeight: "800px",
    }), [placeholder, bottoniDefault, interlineaFinale]);

    const defaultContainerStyle = {
        height: '120vh',
        display: 'flex',
        flexDirection: 'column'
    };

    const styleEditor = adattaStyle ? defaultContainerStyle : null;

    function isTransparentColor(c) {
        if (!c) return true;
        const x = c.trim().toLowerCase();
        if (x === "transparent") return true;

        // rgba(0,0,0,0) o simili
        const m = x.match(/^rgba?\(([^)]+)\)$/);
        if (m) {
            const parts = m[1].split(",").map(s => s.trim());
            // rgba -> alpha è il 4° valore
            if (parts.length === 4) {
                const a = parseFloat(parts[3]);
                if (!isNaN(a) && a === 0) return true;
            }
        }
        return false;
    }

    function normalizeTableBorders(html) {
        const doc = new DOMParser().parseFromString(html, "text/html");

        doc.querySelectorAll("table").forEach((table) => {
            if (!table.style.borderCollapse) table.style.borderCollapse = "collapse";
        });

        doc.querySelectorAll("td, th").forEach((cell) => {
            const styleAttr = (cell.getAttribute("style") || "").toLowerCase();

            // ✅ vero: brushCell ha scritto border-color inline
            const hasInlineBorderColor = styleAttr.includes("border-color");

            if (!hasInlineBorderColor) return;

            const color = cell.style.borderColor; // ora ha senso leggerlo
            if (isTransparentColor(color)) return;

            // se non c’è border style/width, lo rendiamo “stampabile”
            if (!cell.style.borderStyle || !cell.style.borderWidth) {
                cell.style.border = `1px solid ${color}`;
            }
        });

        return doc.body.innerHTML;
    }

    return (
        <div className='editorj-jodit' style={styleEditor}>
            <JoditEditor
                ref={editor}
                value={value}
                config={config}
                onChange={handleEditorChange}
                style={{ flexGrow: 1, width: '100%' }}
            />
        </div>
    );
};

export default EditorJ;
